import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { EChartsInstance } from 'echarts-for-react';
import GIFEncoder from 'gif-encoder-2';
import { isEmpty } from 'lodash/fp';

import { FlexColumn, Loading, Modal, ModalBody, ModalHeader } from '@portal/common/components';
import { isGifExportEnabled as getIsGifExportEnabled } from '@portal/common/models/organization';
import { gifIcon } from '@portal/common/theme/icons';
import { DataExportFormat } from '@portal/common/types';
import delay from '@portal/common/utility/delay';

import _ from '../../../locale';
import { getOrganization } from '../../../store/organization/selectors';
import {
    RENDER_DELAY,
    animateChartTo,
    createGifEncoder,
    downloadGif,
    getTimelineFrames,
} from './animation-utils';

interface AnimationState {
    currentFrameIndex: number;
    animationStartedFromIndex: number;
    frames: number[];
}

interface AnimatableChartProps {
    onExportData?: (format: DataExportFormat) => void;
    saveAsGIF?: {
        visible: boolean;
        enabled: boolean;
        icon: string;
        onClick: () => void;
    };
}

export default function index<T extends AnimatableChartProps>(
    ChartComponent: React.ComponentType<T>
): React.FC<T> {
    return function AnimatableChart(props: T) {
        const organization = useSelector(getOrganization);

        const chartRef = useRef<EChartsInstance | null>(null);
        const encoderRef = useRef<GIFEncoder | null>(null);
        const timeoutRef = useRef<Timeout | null>(null);
        const animationState = useRef<AnimationState | null>(null);

        const [isGenerating, setIsGenerating] = useState(false);

        const getChartWrapperInstance = useCallback((): any => chartRef.current || null, []);

        const getChartInstance = useCallback(
            (): EChartsInstance | null =>
                getChartWrapperInstance()?.chartRef?.current?.chartInstance || null,
            [getChartWrapperInstance]
        );

        const getFilename = useCallback(
            () =>
                getChartWrapperInstance()?.getSaveFilename?.(
                    getChartWrapperInstance()?.chartRef?.current?.getRenderProps?.()
                ),
            [getChartWrapperInstance, getChartInstance]
        );

        const cleanup = useCallback(() => {
            const instance = getChartInstance();
            if (instance) {
                instance.off('finished');
            }
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
            if (animationState.current) {
                animateChartTo(instance, animationState.current.animationStartedFromIndex);
                animationState.current = null;
            }
            encoderRef.current = null;
            setIsGenerating(false);
        }, [getChartInstance]);

        useEffect(() => cleanup, [cleanup]);

        const captureFrame = useCallback(async () => {
            const instance = getChartInstance();
            const encoder = encoderRef.current;
            const state = animationState.current;

            if (!instance || !encoder || !state) return;

            const canvas =
                instance.renderToCanvas?.() || instance.getDom()?.querySelector('canvas');
            if (!canvas) return;

            const ctx = canvas.getContext('2d', { willReadFrequently: true });
            if (!ctx) return;

            encoder.addFrame(ctx);

            if (state.currentFrameIndex >= state.frames.length - 1) {
                downloadGif(encoder, getFilename());
                cleanup();
                return;
            }

            state.currentFrameIndex++;
            animateChartTo(instance, state.frames[state.currentFrameIndex]);

            timeoutRef.current = setTimeout(() => {
                captureFrame();
            }, RENDER_DELAY);
        }, [getChartInstance, cleanup]);

        const startRecording = useCallback(() => {
            const instance = getChartInstance();
            if (!instance) return;

            const canvas =
                instance.renderToCanvas?.() || instance.getDom()?.querySelector('canvas');
            if (!canvas) return;

            const frames = getTimelineFrames(instance);
            if (isEmpty(frames)) return;

            setIsGenerating(true);
            encoderRef.current = createGifEncoder(canvas);

            animationState.current = {
                currentFrameIndex: 0,
                animationStartedFromIndex: instance.getOption()?.timeline?.[0]?.currentIndex ?? 0,
                frames,
            };

            animateChartTo(instance, frames[0]);

            timeoutRef.current = setTimeout(() => {
                captureFrame();
            }, RENDER_DELAY);
        }, [getChartInstance, captureFrame]);

        return (
            <>
                <ChartComponent
                    ref={chartRef}
                    {...props}
                    saveAsGIF={{
                        visible: true,
                        enabled: getIsGifExportEnabled(organization),
                        icon: gifIcon,
                        onClick: startRecording,
                    }}
                />
                <Modal show={isGenerating} onHide={cleanup}>
                    <ModalHeader closeButton />
                    <ModalBody>
                        <FlexColumn itemsSpacing={20}>
                            <div>{_('chart_gif_animation_in_progress')}</div>
                            <Loading />
                        </FlexColumn>
                    </ModalBody>
                </Modal>
            </>
        );
    };
}
